"use client";

import React, { createContext, useContext, useEffect, useState } from 'react';

interface CurrencyContextType {
  selectedCurrency: string;
  setSelectedCurrency: (currency: string) => void;
  formatPrice: (priceUSD: number) => string;
  loading: boolean;
  availableCurrencies: string[];
  exchangeRates: Record<string, number>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);
const CURRENCY_STORAGE_KEY = 'selectedCurrency';

export const CurrencyProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({ USD: 1 });
  const [loading, setLoading] = useState(true);

  // Initialize currency from localStorage (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);
      if (savedCurrency) {
        setSelectedCurrency(savedCurrency);
      }
    }
  }, []);

  // Save currency to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(CURRENCY_STORAGE_KEY, selectedCurrency);
    }
  }, [selectedCurrency]);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(`https://api.exchangerate-api.com/v4/latest/USD`);
        const data = await response.json();

        if (data.rates) {
          setExchangeRates(data.rates);
        } else {
          throw new Error('Invalid API response');
        }
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
        setExchangeRates({ USD: 1 });
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []); // Only fetch once on mount

  // Validate selected currency exists in exchange rates
  useEffect(() => {
    if (Object.keys(exchangeRates).length > 0 && !exchangeRates[selectedCurrency]) {
      setSelectedCurrency('USD');
    }
  }, [exchangeRates, selectedCurrency]);

  const formatPriceWithCurrency = (priceUSD: number): string => {
    if (!priceUSD || priceUSD <= 0) return '';

    const formatOptions: Record<string, { locale: string; currency: string }> = {
      USD: { locale: 'en-US', currency: 'USD' },
      EUR: { locale: 'de-DE', currency: 'EUR' },
      GBP: { locale: 'en-GB', currency: 'GBP' },
      JPY: { locale: 'ja-JP', currency: 'JPY' },
      CAD: { locale: 'en-US', currency: 'CAD' },
      AUD: { locale: 'en-US', currency: 'AUD' },
      HKD: { locale: 'en-US', currency: 'HKD' },
      CNY: { locale: 'en-US', currency: 'CNY' },
      CHF: { locale: 'en-US', currency: 'CHF' },
    };

    const { locale, currency: currencyCode } = formatOptions[selectedCurrency] || formatOptions.USD;
    const exchangeRate = exchangeRates[selectedCurrency] || 1;
    const convertedPrice = priceUSD * exchangeRate;

    // Check if the converted price has a fractional part
    const hasFraction = convertedPrice % 1 !== 0;

    const options: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    };

    return new Intl.NumberFormat(locale, options).format(convertedPrice);
  };

  const value: CurrencyContextType = {
    selectedCurrency,
    setSelectedCurrency,
    formatPrice: formatPriceWithCurrency,
    loading,
    availableCurrencies: Object.keys(exchangeRates),
    exchangeRates,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};
